/**
 * Product page linking for upsell offers (title + image clicks).
 * Default: enabled when `linkToProduct` is unset; opens in new tab when
 * `linkToProductOpenInNewTab` is unset.
 */

export const PRODUCT_LINK_ATTRIBUTION_PARAM = "weupsell_pl";

/**
 * Compact base64url JSON token for PDP revenue attribution.
 * @param {{
 *   popupId?: string | number;
 *   campaignId?: string | number;
 *   productId?: string | number;
 *   popupType?: string;
 *   shop?: string | { myshopifyDomain?: string; shop?: string };
 * }} [params]
 * @returns {string | null}
 */
export function buildProductLinkAttributionToken(params = {}) {
  const popupId =
    params.popupId != null && params.popupId !== ""
      ? String(params.popupId)
      : "";
  if (!popupId) {
    return null;
  }

  const shop =
    typeof params.shop === "string"
      ? params.shop
      : params.shop?.myshopifyDomain || params.shop?.shop || "";

  const productIdRaw =
    params.productId != null && params.productId !== ""
      ? String(params.productId)
      : "";
  const productIdMatch = productIdRaw.match(/(\d+)\s*$/);
  const productId = productIdMatch ? productIdMatch[1] : productIdRaw;

  const payload = {
    popupId,
    campaignId:
      params.campaignId != null && params.campaignId !== ""
        ? String(params.campaignId)
        : "",
    productId,
    popupType:
      params.popupType != null && params.popupType !== ""
        ? String(params.popupType)
        : "",
    shop: String(shop || ""),
  };

  try {
    const json = JSON.stringify(payload);
    const bytes =
      typeof TextEncoder !== "undefined"
        ? new TextEncoder().encode(json)
        : null;
    let binary = "";
    if (bytes) {
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
    } else if (typeof Buffer !== "undefined") {
      return Buffer.from(json, "utf8")
        .toString("base64")
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
    } else {
      binary = unescape(encodeURIComponent(json));
    }
    const b64 =
      typeof btoa !== "undefined"
        ? btoa(binary)
        : Buffer.from(binary, "binary").toString("base64");
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return null;
  }
}

/**
 * @param {string} url
 * @param {string | null | undefined} token
 * @returns {string}
 */
export function appendUpsellAttributionParam(url, token) {
  const base = String(url || "").trim();
  const value = String(token || "").trim();
  if (!base || !value) {
    return base;
  }
  if (
    new RegExp(`[?&]${PRODUCT_LINK_ATTRIBUTION_PARAM}=`, "i").test(base)
  ) {
    return base;
  }
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${PRODUCT_LINK_ATTRIBUTION_PARAM}=${encodeURIComponent(value)}`;
}

/**
 * @param {string | object | null | undefined} attribution
 * @returns {string | null}
 */
function resolveAttributionToken(attribution) {
  if (!attribution) {
    return null;
  }
  if (typeof attribution === "string") {
    return attribution.trim() || null;
  }
  if (typeof attribution === "object") {
    return buildProductLinkAttributionToken(attribution);
  }
  return null;
}

/**
 * Build PDP attribution token from popup + product (checkout / thank-you /
 * order-status / post-purchase title & image clicks).
 * Mirrors weupsell-preview `useProductLink` attribution.
 *
 * @param {object | null | undefined} popup
 * @param {object | null | undefined} product
 * @param {string | object | null | undefined} [shop]
 * @returns {string | null}
 */
export function buildProductLinkAttributionFromPopup(
  popup,
  product,
  shop = ""
) {
  if (!popup || !product) {
    return null;
  }
  return buildProductLinkAttributionToken({
    popupId: popup._id ?? popup.id ?? popup.popupId,
    campaignId: popup.campaign_Id,
    productId: product.id ?? product.admin_graphql_api_id,
    popupType: popup.popUpType,
    shop: shop || "",
  });
}

export function getProductLinkSettings(source) {
  const product =
    source?.generalSettings?.condition?.product ||
    source?.condition?.product ||
    (source?.linkToProduct !== undefined ||
    source?.linkToProductOpenInNewTab !== undefined
      ? source
      : null);

  if (!product || typeof product !== "object") {
    return { enabled: true, openInNewTab: true };
  }

  return {
    enabled: product.linkToProduct !== false,
    openInNewTab: product.linkToProductOpenInNewTab !== false,
  };
}

export function isLinkToProductEnabled(source) {
  return getProductLinkSettings(source).enabled;
}

export function isLinkToProductOpenInNewTab(source) {
  return getProductLinkSettings(source).openInNewTab;
}

/**
 * Link props for Shopify checkout/post-purchase extension `Link` components.
 * `external: true` opens in a new tab; `external: false` navigates in the same tab.
 *
 * @returns {{ to: string, external: boolean } | null}
 */
export function resolveExtensionProductLink(source, product, options = {}) {
  if (!isLinkToProductEnabled(source)) {
    return null;
  }

  const storefrontUrl = String(options.storefrontUrl || "")
    .trim()
    .replace(/\/$/, "");
  const handle = resolveProductHandle(product);
  let url = null;

  if (handle && storefrontUrl) {
    url = `${storefrontUrl}/products/${handle}`;
    const token = resolveAttributionToken(options.attribution);
    if (token) {
      url = appendUpsellAttributionParam(url, token);
    }
  } else {
    url = buildStorefrontProductUrl(product, options);
  }

  if (!url) {
    return null;
  }

  return {
    to: url,
    external: isLinkToProductOpenInNewTab(source),
  };
}

export function resolveProductHandle(product) {
  if (!product || product.isDummy) {
    return null;
  }

  const handle = String(product.handle || "").trim();
  if (handle) {
    return handle;
  }

  const url = String(
    product.url || product.onlineStoreUrl || product.online_store_url || ""
  ).trim();

  if (!url) {
    return null;
  }

  const match = url.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * @param {object} product
 * @param {{ shopDomain?: string, origin?: string, attribution?: string | object }} [options]
 */
export function buildStorefrontProductUrl(product, options = {}) {
  const handle = resolveProductHandle(product);
  if (!handle) {
    return null;
  }

  const path = `/products/${handle}`;
  const shopDomain = String(options.shopDomain || "").trim();
  let url;

  if (shopDomain) {
    const host = shopDomain.includes(".") ? shopDomain : `${shopDomain}.myshopify.com`;
    url = `https://${host}${path}`;
  } else {
    const origin =
      String(options.origin || "").trim() ||
      (typeof window !== "undefined" ? window.location.origin : "");
    url = origin ? `${origin}${path}` : path;
  }

  const token = resolveAttributionToken(options.attribution);
  return token ? appendUpsellAttributionParam(url, token) : url;
}

/**
 * @param {object} product
 * @param {{ attribution?: string | object }} [options]
 */
export function buildStorefrontProductPath(product, options = {}) {
  const handle = resolveProductHandle(product);
  if (!handle) {
    return null;
  }
  const path = `/products/${handle}`;
  const token = resolveAttributionToken(options.attribution);
  return token ? appendUpsellAttributionParam(path, token) : path;
}

export function openProductPage(urlOrProduct, options = {}) {
  let url =
    typeof urlOrProduct === "string"
      ? String(urlOrProduct || "").trim()
      : buildStorefrontProductUrl(urlOrProduct, options);

  if (typeof urlOrProduct === "string") {
    const token = resolveAttributionToken(options.attribution);
    if (token) {
      url = appendUpsellAttributionParam(url, token);
    }
  }

  if (!url || typeof window === "undefined") {
    return false;
  }

  const { newTab = true } = options;

  if (newTab) {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    window.location.assign(url);
  }

  return true;
}

export function resolveCartLineProductUrl(cartItem, options = {}) {
  if (!cartItem || typeof cartItem !== "object") {
    return null;
  }

  const rawUrl = String(cartItem.url || "").trim();
  if (rawUrl) {
    if (/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }

    const origin =
      String(options.origin || "").trim() ||
      (typeof window !== "undefined" ? window.location.origin : "");

    if (!origin) {
      return rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    }

    return `${origin}${rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`}`;
  }

  return buildStorefrontProductUrl(cartItem, options);
}

export function navigateToStorefrontProduct(product, options = {}) {
  const url = buildStorefrontProductUrl(product, options);
  if (!url) {
    return false;
  }

  const { newTab = true } = options;

  if (newTab) {
    return openProductPage(url, { newTab: true });
  }

  if (typeof window !== "undefined") {
    window.location.assign(url);
  }

  return true;
}

